<script setup lang="ts">
import { nextTick, onMounted, ref } from 'vue'
import BaseButton from '@/components/ui/BaseButton.vue'
import '@/components/chat/chatEditing.css'

const props = withDefaults(defineProps<{
  content: string
  label?: string
  contentClass?: string
  disabled?: boolean
  validate?: (content: string) => string | null
  save?: (content: string) => Promise<boolean>
}>(), { label: 'Edit message', contentClass: '', disabled: false })
const emit = defineEmits<{ save: [content: string]; cancel: []; saved: []; input: [content: string] }>()
const editable = ref<HTMLElement | null>(null)
const saving = ref(false)
const error = ref<string | null>(null)

onMounted(async () => {
  await nextTick()
  const el = editable.value
  if (!el) return
  el.textContent = props.content
  el.focus()
  const range = document.createRange()
  range.selectNodeContents(el)
  range.collapse(false)
  const selection = window.getSelection()
  selection?.removeAllRanges()
  selection?.addRange(range)
})

function input() {
  error.value = null
  emit('input', editable.value?.innerText ?? '')
}
async function save() {
  if (props.disabled || saving.value) return
  const content = editable.value?.innerText ?? ''
  error.value = props.validate?.(content) ?? null
  if (error.value) return
  if (content.trim() === props.content) { emit('saved'); return }
  if (!props.save) { emit('save', content); emit('saved'); return }
  saving.value = true
  try {
    if (await props.save(content)) emit('saved')
  } finally { saving.value = false }
}
</script>

<template>
  <div ref="editable" class="chat-inline-editor" :class="contentClass" :contenteditable="disabled || saving ? 'false' : 'plaintext-only'"
    role="textbox" aria-multiline="true" :aria-label="label" :aria-invalid="Boolean(error)"
    @input="input" @keydown.esc.stop.prevent="!saving && emit('cancel')" />
  <slot />
  <p v-if="error" class="chat-inline-editor__error" role="alert">{{ error }}</p>
  <div class="chat-inline-editor__actions">
    <BaseButton variant="success" size="sm" :disabled="disabled || saving" @click="save">Save</BaseButton>
    <BaseButton variant="secondary" size="sm" :disabled="saving" @click="emit('cancel')">Cancel</BaseButton>
  </div>
</template>
